import { keepCurrentOption, displayedHousingType } from '../utils/personInterviewOptions';
import React, { useEffect, useRef, useState } from 'react';
import { FaAllergies, FaHeartbeat, FaUtensils, FaPills, FaHandsHelping, FaWalking, FaBan, FaWheelchair, FaNotesMedical } from 'react-icons/fa';
import './PersonProfilePanels.css';

export const BRAZIL_UFS = Object.freeze(['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO']);
const digits = (value) => String(value || '').replace(/\D/g, '');
export const maskAddressCep = (value) => digits(value).slice(0, 8).replace(/(\d{5})(\d)/, '$1-$2');
const cepFields = { street: 'logradouro', neighborhood: 'bairro', city: 'localidade', state: 'uf' };
export function applyCepAddress(current, cep, snapshot, response) {
  if (!response || response.erro || digits(current.address?.zipCode) !== cep) return current;
  const address = { ...current.address };
  for (const [key, remote] of Object.entries(cepFields)) {
    // Never overwrite a manual edit made while the postal-code lookup was pending.
    if ((address[key] || '') !== (snapshot[key] || '')) continue;
    const value = String(response[remote] || '').trim();
    if (value && (key !== 'state' || BRAZIL_UFS.includes(value))) address[key] = value;
  }
  return { ...current, address };
}

export function RecordAddressFields({ form, setForm, Input }) {
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const request = useRef({ sequence: 0, controller: null });
  const alive = useRef(true);
  const addressRef = useRef(form.address || {});
  const setFormRef = useRef(setForm);
  addressRef.current = form.address || {};
  setFormRef.current = setForm;
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; request.current.sequence += 1; request.current.controller?.abort(); };
  }, []);
  const setAddress = (key, value) => setFormRef.current((current) => ({ ...current, address: { ...(current.address || {}), [key]: value } }));
  const fillCep = async (value) => {
    const masked = maskAddressCep(value);
    const cep = digits(masked);
    const sequence = ++request.current.sequence;
    request.current.controller?.abort();
    setAddress('zipCode', masked);
    setFeedback('');
    setLoading(false);
    if (cep.length !== 8) return;
    const controller = new AbortController();
    request.current.controller = controller;
    const snapshot = { ...addressRef.current };
    setLoading(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`, { signal: controller.signal });
      if (!response.ok) throw new Error('CEP indisponivel');
      const data = await response.json();
      if (!alive.current || sequence !== request.current.sequence) return;
      if (data.erro) setFeedback('CEP n\u00e3o encontrado. Preencha o endere\u00e7o manualmente.');
      else setFormRef.current((current) => applyCepAddress(current, cep, snapshot, data));
    } catch (error) {
      if (alive.current && sequence === request.current.sequence && error.name !== 'AbortError') setFeedback('Consulta de CEP indispon\u00edvel. Preencha o endere\u00e7o manualmente.');
    } finally {
      if (alive.current && sequence === request.current.sequence) setLoading(false);
    }
  };
  const address = form.address || {};
  // Preserve any legacy value without silently rewriting the saved record.
  const state = address.state || '';
  const states = [{ value: '', label: '' }, ...BRAZIL_UFS.map((uf) => ({ value: uf, label: uf }))];
  if (state && !BRAZIL_UFS.includes(state)) states.push({ value: state, label: state });
  const field = (name) => `record-profile__address-field record-profile__field--${name}`;
  return <>
    <Input wrapperClassName={field('zipCode')} label="CEP" value={address.zipCode || ''} onChange={(e) => fillCep(e.target.value)} inputMode="numeric" autoComplete="postal-code" maxLength={9} placeholder="00000-000" aria-busy={loading} title={loading ? 'Consultando CEP...' : (feedback || 'CEP')} />
    <Input wrapperClassName={field('street')} label="Rua" value={address.street || ''} onChange={(e) => setAddress('street', e.target.value)} autoComplete="address-line1" />
    <Input wrapperClassName={field('number')} label={'N\u00famero'} value={address.number || ''} onChange={(e) => setAddress('number', e.target.value)} />
    <Input wrapperClassName={field('complement')} label="Complemento" value={address.complement || ''} onChange={(e) => setAddress('complement', e.target.value)} autoComplete="address-line2" />
    <Input wrapperClassName={field('neighborhood')} label="Bairro" value={address.neighborhood || ''} onChange={(e) => setAddress('neighborhood', e.target.value)} />
    <Input wrapperClassName={field('city')} label="Cidade" value={address.city || ''} onChange={(e) => setAddress('city', e.target.value)} autoComplete="address-level2" />
    <Input wrapperClassName={field('state')} label="Estado" value={state} onChange={(e) => setAddress('state', e.target.value)} options={states} autoComplete="address-level1" />
    <Input wrapperClassName={field('housingType')} label="Tipo de moradia" value={displayedHousingType(address.housingType)} onChange={(e) => setAddress('housingType', e.target.value)} options={keepCurrentOption(['', 'Pr\u00f3pria', 'Aluguel', 'Cedida', 'Financiada', 'Ocupa\u00e7\u00e3o', 'Situa\u00e7\u00e3o de rua', 'Outro'], displayedHousingType(address.housingType))} />
    <span className="record-profile__sr-only" role="status">{loading ? 'Consultando CEP...' : feedback}</span>
  </>;
}

const healthIcons = {
  allergy: FaAllergies, healthIssue: FaHeartbeat, foodRestriction: FaUtensils,
  controlledMedication: FaPills, activitySupport: FaHandsHelping,
  physicalLimitation: FaWalking, psychoactiveSubstances: FaBan, disability: FaWheelchair,
};
export function RecordHealthIcon({ field }) {
  const Icon = healthIcons[field] || FaNotesMedical;
  return <span className="record-profile__health-icon"><Icon aria-hidden="true" focusable="false" /></span>;
}

/* interview-housing-v1 */
